import { t } from "@lingui/macro";
import {
  ArrowRight,
  ArrowUp,
  ChatCircle,
  Crown,
  Gear,
  ReadCvLogo,
  Sparkle,
} from "@phosphor-icons/react";
import { Button, KeyboardShortcut, Separator } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useKeyboardShortcut from "use-keyboard-shortcut";

import { Copyright } from "@/client/components/copyright";
import { Icon } from "@/client/components/icon";
import { UserAvatar } from "@/client/components/user-avatar";
import { UserOptions } from "@/client/components/user-options";
import { useUser } from "@/client/services/user";
import { useBillingStore } from "@/client/stores/billing";

/* eslint-disable lingui/no-unlocalized-strings */
const TIER_LABELS: Record<string, { name: string; color: string }> = {
  starter: { name: "Starter", color: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  pro: { name: "Pro", color: "bg-violet-500/10 text-violet-600 border-violet-500/20" },
  careerMax: { name: "Career Max", color: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
  ultra: { name: "Ultra", color: "bg-rose-500/10 text-rose-600 border-rose-500/20" },
};
/* eslint-enable lingui/no-unlocalized-strings */

type Props = {
  className?: string;
};

const ActiveIndicator = ({ className }: Props) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    className={cn(
      "h-1.5 w-1.5 animate-pulse rounded-full bg-info shadow-[0_0_12px] shadow-info",
      className,
    )}
  />
);

interface SidebarItem {
  path: string;
  name: string;
  shortcut?: string;
  icon: React.ReactNode;
}

type SidebarItemProps = SidebarItem & {
  onClick?: () => void;
};

const SidebarItem = ({ path, name, shortcut, icon, onClick }: SidebarItemProps) => {
  const isActive = useLocation().pathname === path;

  return (
    <Button
      asChild
      size="lg"
      variant="ghost"
      onClick={onClick}
      className={cn(
        "h-auto justify-start px-4 py-3",
        isActive && "pointer-events-none bg-secondary/50 text-secondary-foreground",
      )}
    >
      <Link to={path}>
        <div className="mr-3">{icon}</div>
        <span>{name}</span>
        {!isActive && <KeyboardShortcut className="ml-auto">{shortcut}</KeyboardShortcut>}
        {isActive && <ActiveIndicator className="ml-auto" />}
      </Link>
    </Button>
  );
};

type SidebarProps = {
  setOpen?: (open: boolean) => void;
};

export const Sidebar = ({ setOpen }: SidebarProps) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const openPaywall = useBillingStore((s) => s.openPaywall);

  useKeyboardShortcut(["shift", "r"], () => {
    navigate("/dashboard/resumes");
    setOpen?.(false);
  });

  useKeyboardShortcut(["shift", "s"], () => {
    navigate("/dashboard/settings");
    setOpen?.(false);
  });

  const sidebarItems: SidebarItem[] = [
    {
      path: "/dashboard/resumes",
      name: t`Resumes`,
      shortcut: "⇧R",
      icon: <ReadCvLogo />,
    },
    {
      path: "/dashboard/settings",
      name: t`Settings`,
      shortcut: "⇧S",
      icon: <Gear />,
    },
    {
      path: "/contact",
      name: t`Contact Us`,
      icon: <ChatCircle />,
    },
  ];

  return (
    <div className="flex h-full flex-col gap-y-4">
      <div className="ml-12 flex justify-center lg:ml-0">
        <Button asChild size="icon" variant="ghost" className="h-10 w-10 p-0">
          <Link to="/">
            <Icon size={24} className="mx-auto hidden lg:block" />
          </Link>
        </Button>
      </div>

      <Separator className="opacity-50" />

      <div className="grid gap-y-2">
        {sidebarItems.map((item) => (
          <SidebarItem {...item} key={item.path} onClick={() => setOpen?.(false)} />
        ))}
      </div>

      <div className="flex-1" />

      {/* Plan badge */}
      {user?.planTier && TIER_LABELS[user.planTier] ? (
        <div className="mx-2 space-y-1.5">
          <Link
            to="/dashboard/settings"
            className={cn(
              "flex items-center gap-x-2 rounded-lg border px-3 py-2 transition-colors hover:bg-secondary/50",
              user.planCancelAtPeriodEnd
                ? "border-warning/30 bg-warning/5 text-warning"
                : TIER_LABELS[user.planTier].color,
            )}
          >
            <Crown size={16} weight="fill" />
            <span className="text-xs font-semibold">
              {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
              {TIER_LABELS[user.planTier].name}
              {user.planCancelAtPeriodEnd && (
                <span className="ml-1 font-normal opacity-70">
                  {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
                  (ending)
                </span>
              )}
            </span>
            {user.planCurrentPeriodEnd && (
              <span className="ml-auto text-[10px] opacity-60">
                {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
                {Math.max(
                  0,
                  Math.ceil(
                    (new Date(user.planCurrentPeriodEnd).getTime() - Date.now()) /
                      (1000 * 60 * 60 * 24),
                  ),
                )}
                d
              </span>
            )}
          </Link>
          {user.planTier !== "ultra" && !user.planCancelAtPeriodEnd && (
            <button
              onClick={() => openPaywall(undefined, "Upgrade to a higher plan for more features.")}
              className="flex w-full items-center justify-center gap-x-1.5 rounded-md px-3 py-1.5 text-[11px] font-medium text-primary transition-colors hover:bg-primary/10"
            >
              <ArrowUp size={12} weight="bold" />
              {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
              Upgrade Plan
            </button>
          )}
        </div>
      ) : (
        <button
          onClick={() =>
            openPaywall(
              undefined,
              t`Subscribe to unlock the resume builder, AI tools, and exports.`,
            )
          }
          className={cn(
            "group relative mx-2 overflow-hidden rounded-xl border border-primary/40 px-4 py-3 text-left transition-all",
            "bg-gradient-to-br from-primary/20 via-primary/10 to-primary/5",
            "shadow-[0_0_0_1px_rgba(0,0,0,0.02)] hover:border-primary/60 hover:shadow-lg hover:shadow-primary/20",
          )}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -inset-0.5 -z-10 rounded-xl bg-gradient-to-br from-primary/30 to-transparent opacity-0 blur-md transition-opacity group-hover:opacity-100"
          />

          <div className="flex items-center gap-x-3">
            <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
              <Crown size={18} weight="fill" />
              <motion.span
                aria-hidden="true"
                animate={{ scale: [1, 1.4, 1], opacity: [0.6, 0, 0.6] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-0 rounded-lg bg-primary/40"
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-x-1">
                <Sparkle size={11} weight="fill" className="text-primary" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
                  {t`Locked`}
                </span>
              </div>
              <p className="mt-0.5 text-sm font-semibold text-foreground">{t`Unlock CareerBldr`}</p>
              <p className="text-xs leading-snug text-foreground/60">
                {t`Start a 3-day trial`}
              </p>
            </div>
          </div>

          <div className="mt-3 flex h-8 items-center justify-center gap-x-1.5 rounded-md bg-primary text-xs font-semibold text-primary-foreground shadow-sm transition-transform group-hover:scale-[1.02]">
            {t`Start 3-day trial`}
            <ArrowRight size={12} weight="bold" />
          </div>
        </button>
      )}

      <Separator className="opacity-50" />

      <UserOptions>
        <Button size="lg" variant="ghost" className="w-full justify-start px-3">
          <UserAvatar size={24} className="mr-3" />
          <span>{user?.name}</span>
        </Button>
      </UserOptions>

      <Copyright className="ml-2" />
    </div>
  );
};
